import type { CashoutRequest, User } from '../dataProvider/types';

/**
 * The tax-reporting query/aggregation module (D-tax-reporting, Phase 7 surface 4) - the ONE place
 * the 1099-K report is shaped. Same seam idiom as the DSAR / sanctions models: v1 aggregates
 * client-side from the mock provider, every selector lives here, and the real-data migration swaps
 * this module for a server-side endpoint without the surface changing.
 *
 * This is a PURE DERIVED READ. The reportable members are computed on read from the paid payout
 * records (the deriveBalance filter-and-reduce pattern), NOT a stored reportable-total: the
 * platform holds the payouts, so the threshold crossing is an arithmetic FACT about them, never a
 * stored figure that can drift. Nothing here writes; the report IS the deliverable (the
 * assemble-and-display posture of buildDsarExportPackage). A real 1099-K generation and IRS filing
 * is the cross-repo migration seam.
 *
 * US-ONLY / SINGLE-JURISDICTION: the 1099-K is a US IRS form with no UK equivalent, so this
 * surface is structurally fixed to `market === 'us'` (a plain equality filter baked into the
 * derivation), NOT a region facet and NOT the switcher. This is the deliberate INVERSE of the
 * oversight-global-with-a-facet posture of DSAR, consent and sanctions: it is fixed to US because
 * the OBLIGATION is US-only, distinct from a region scope a UK officer cannot widen.
 *
 * DONATIONS ARE EXCLUDED: a paid donation is money to a charity, not a third-party payment to the
 * member, so it is not 1099-K reportable. The derivation counts `destinationType === 'payout'`
 * only.
 */

/**
 * The IRS 1099-K reporting threshold per tax YEAR, in USD. A per-year reviewable lookup, NOT one
 * hardcoded constant: the threshold has changed year over year (the phased ARPA rollout), so a
 * single constant would be wrong for every year but one. [REVIEW: confirm each year's figure with
 * counsel; the IRS has repeatedly revised the phase-in.]
 */
export const THRESHOLD_BY_YEAR: Record<number, number> = {
  2023: 20000,
  2024: 5000,
  2025: 2500,
  2026: 600,
};

/**
 * The fallback threshold for a year not in the lookup, the statutory floor the phase-in lands on.
 * [REVIEW: track future-year figures as the IRS confirms them.]
 */
export const DEFAULT_THRESHOLD = 600;

/**
 * The reporting threshold that applies to a given tax year. Reads from the supplied
 * per-year map, defaulting to the statutory THRESHOLD_BY_YEAR when no map is passed
 * (the pre-S4 callers). Since S4 (D-settings-s4-us-compliance) the tax report passes the
 * compliance CONFIG map MERGED over THRESHOLD_BY_YEAR, so config wins per year and any
 * year the config omits falls back to the statutory figure, then to DEFAULT_THRESHOLD.
 */
export const thresholdForYear = (
  year: number,
  thresholds: Record<number, number> = THRESHOLD_BY_YEAR,
): number => thresholds[year] ?? DEFAULT_THRESHOLD;

/** One reportable member: a US member whose paid payouts in the tax year cleared the threshold. */
export interface TaxReportRow {
  userId: number;
  memberName: string;
  memberEmail: string;
  /** Always 'us' here: the report is single-jurisdiction by construction. */
  market: 'us';
  taxYear: number;
  /** Sum of the member's paid payout amounts (USD) bucketed into the tax year. */
  reportableTotal: number;
  /** How many paid payouts made up the total. */
  payoutCount: number;
  /** The year's reporting threshold the total was tested against. */
  threshold: number;
}

export interface TaxReportView {
  taxYear: number;
  threshold: number;
  /** US members at or over the threshold for the year, highest total first. */
  reportable: TaxReportRow[];
  /** The tax years that have any paid US payout data, most recent first (the year selector). */
  availableYears: number[];
}

export interface TaxReportInput {
  cashoutRequests: CashoutRequest[];
  users: User[];
}

export interface TaxReportOptions {
  /** The tax year to report. */
  taxYear: number;
  /**
   * The per-year reporting thresholds READ FROM the compliance config (D-settings-s4).
   * Merged over THRESHOLD_BY_YEAR by the caller (useTaxReport) so an absent year falls
   * back to the statutory constant. Omitted (the pre-S4 callers) means the constant is
   * the source, so the report never renders a blank threshold.
   */
  thresholds?: Record<number, number>;
}

/** The calendar year a paid payout falls into, off its paidAt timestamp. */
const yearOf = (paidAt: string): number => new Date(paidAt).getUTCFullYear();

/**
 * The paid US payouts in scope for any tax-year computation: status paid (the money left),
 * market us (single-jurisdiction), destinationType payout (donations excluded), and a non-null
 * paidAt (so it can be bucketed into a calendar year).
 */
const reportablePayouts = (
  cashoutRequests: CashoutRequest[],
): CashoutRequest[] =>
  cashoutRequests.filter(
    (c) =>
      c.market === 'us' &&
      c.status === 'paid' &&
      c.destinationType === 'payout' &&
      c.paidAt != null,
  );

export function buildTaxReportView(
  input: TaxReportInput,
  opts: TaxReportOptions,
): TaxReportView {
  const { taxYear } = opts;
  const threshold = thresholdForYear(taxYear, opts.thresholds);
  const usersById = new Map(input.users.map((u) => [u.id, u]));
  const paid = reportablePayouts(input.cashoutRequests);

  // The selectable years: every year that has paid US payout data, most recent first.
  const availableYears = Array.from(
    new Set(paid.map((c) => yearOf(c.paidAt as string))),
  ).sort((a, b) => b - a);

  // Per-member total for the selected year (the deriveBalance filter-and-reduce).
  const totals = new Map<number, { total: number; count: number }>();
  for (const c of paid) {
    if (yearOf(c.paidAt as string) !== taxYear) continue;
    const acc = totals.get(c.userId) ?? { total: 0, count: 0 };
    acc.total = Math.round((acc.total + Number(c.amount)) * 100) / 100;
    acc.count += 1;
    totals.set(c.userId, acc);
  }

  const reportable: TaxReportRow[] = [];
  for (const [userId, acc] of totals) {
    if (acc.total < threshold) continue;
    const user = usersById.get(userId);
    reportable.push({
      userId,
      memberName: user?.name ?? '',
      memberEmail: user?.email ?? '',
      market: 'us',
      taxYear,
      reportableTotal: acc.total,
      payoutCount: acc.count,
      threshold,
    });
  }
  reportable.sort(
    (a, b) => b.reportableTotal - a.reportableTotal || a.userId - b.userId,
  );

  return { taxYear, threshold, reportable, availableYears };
}
